import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as api from '../../../api';
import Section from '../../Atoms/Section';
import H1 from '../../Atoms/H1';
import Spinner from '../../Atoms/Spinner';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MonitoringContainer = styled.div`
  padding: 1.5rem 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f1f3f4;
  padding: 4px;
  border-radius: 12px;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? '#fff' : 'transparent'};
  color: ${props => props.active ? '#1a1a1a' : '#666'};
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    background: ${props => props.active ? '#fff' : '#e8eaeb'};
  }
`;

const YearSelector = styled.select`
  padding: 0.75rem 1.25rem;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1a1a1a;
  }

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const StatCard = styled.div`
  background: ${props => props.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: 16px;
  padding: 1.75rem;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const StatIcon = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2.5rem;
  opacity: 0.3;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const StatPercentage = styled.span`
  font-size: 1rem;
  font-weight: 600;
  background: rgba(255,255,255,0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-left: 0.5rem;
`;

const StatSubtext = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
  margin-top: 0.5rem;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ChartTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const ChartFilter = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.85rem;
  background: #f8f9fa;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const BreakdownSection = styled.div`
  background: #f8f9fa;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const BreakdownTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  span {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const BreakdownItem = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.color || '#667eea'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const BreakdownLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const BreakdownValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
`;

const DoughnutContainer = styled.div`
  max-width: 280px;
  margin: 0 auto;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #444;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  gap: 1rem;
`;

const LoadingText = styled.div`
  font-size: 1rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin: 2rem 0;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Monitoring = ({ fullUser, fullUserLoading }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [yearlyAnalytics, setYearlyAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartView, setChartView] = useState('users');

  useEffect(() => {
    if (!fullUserLoading && (!fullUser?.user || fullUser.user.role !== 'admin')) {
      navigate('/');
    }
  }, [fullUser, fullUserLoading, navigate]);

  useEffect(() => {
    if (!fullUserLoading && fullUser?.user?.role === 'admin') {
      fetchData();
    }
  }, [fullUser, fullUserLoading, activeTab, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'weekly') {
        const response = await api.GetPlatformAnalytics();
        setAnalytics(response.data);
      } else {
        const response = await api.GetYearlyAnalytics(selectedYear);
        setYearlyAnalytics(response.data);
      }
    } catch (err) {
      setError('Échec du chargement des données analytiques');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2023; y--) {
      years.push(y);
    }
    return years;
  }, []);

  const userTypeChartData = useMemo(() => {
    if (!yearlyAnalytics) return null;
    return {
      labels: ['Fabricants/Producteurs', 'Créateurs'],
      datasets: [{
        data: [
          yearlyAnalytics.users.fabricants.total,
          yearlyAnalytics.users.createurs.total
        ],
        backgroundColor: [
          'rgba(102, 126, 234, 0.9)',
          'rgba(118, 75, 162, 0.9)'
        ],
        borderColor: ['#667eea', '#764ba2'],
        borderWidth: 2,
        hoverOffset: 8
      }]
    };
  }, [yearlyAnalytics]);

  const projectTypeChartData = useMemo(() => {
    if (!yearlyAnalytics) return null;
    return {
      labels: ['Objet', 'Espace'],
      datasets: [{
        data: [
          yearlyAnalytics.projects.objet.total,
          yearlyAnalytics.projects.espace.total
        ],
        backgroundColor: [
          'rgba(46, 204, 113, 0.9)',
          'rgba(52, 152, 219, 0.9)'
        ],
        borderColor: ['#2ecc71', '#3498db'],
        borderWidth: 2,
        hoverOffset: 8
      }]
    };
  }, [yearlyAnalytics]);

  const monthlyChartData = useMemo(() => {
    if (!yearlyAnalytics) return null;

    if (chartView === 'users') {
      return {
        labels: yearlyAnalytics.charts.labels,
        datasets: [
          {
            label: 'Total Utilisateurs',
            data: yearlyAnalytics.charts.users.total,
            borderColor: '#667eea',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#667eea',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
          },
          {
            label: 'Fabricants',
            data: yearlyAnalytics.charts.users.fabricants,
            borderColor: '#2ecc71',
            backgroundColor: 'transparent',
            tension: 0.4,
            pointBackgroundColor: '#2ecc71',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 3,
          },
          {
            label: 'Créateurs',
            data: yearlyAnalytics.charts.users.createurs,
            borderColor: '#9b59b6',
            backgroundColor: 'transparent',
            tension: 0.4,
            pointBackgroundColor: '#9b59b6',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 3,
          }
        ]
      };
    } else {
      return {
        labels: yearlyAnalytics.charts.labels,
        datasets: [
          {
            label: 'Total Projets',
            data: yearlyAnalytics.charts.projects.total,
            backgroundColor: 'rgba(52, 152, 219, 0.8)',
            borderColor: '#3498db',
            borderWidth: 1,
            borderRadius: 6,
          },
          {
            label: 'Objet',
            data: yearlyAnalytics.charts.projects.objet,
            backgroundColor: 'rgba(46, 204, 113, 0.8)',
            borderColor: '#2ecc71',
            borderWidth: 1,
            borderRadius: 6,
          },
          {
            label: 'Espace',
            data: yearlyAnalytics.charts.projects.espace,
            backgroundColor: 'rgba(155, 89, 182, 0.8)',
            borderColor: '#9b59b6',
            borderWidth: 1,
            borderRadius: 6,
          }
        ]
      };
    }
  }, [yearlyAnalytics, chartView]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: '500' }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 14, weight: '600' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { size: 11 } }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 14, weight: '600' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (fullUserLoading || loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>Chargement des statistiques...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!fullUser?.user || fullUser.user.role !== 'admin') {
    return null;
  }

  if (error) {
    return (
      <Section className="padding">
        <H1>Monitoring</H1>
        <ErrorMessage>{error}</ErrorMessage>
      </Section>
    );
  }

  return (
    <Section className="padding">
      <Header>
        <H1 style={{ margin: 0 }}>Monitoring</H1>
        <TabContainer>
          <Tab active={activeTab === 'yearly'} onClick={() => setActiveTab('yearly')}>
            Annuel
          </Tab>
          <Tab active={activeTab === 'weekly'} onClick={() => setActiveTab('weekly')}>
            Hebdomadaire
          </Tab>
        </TabContainer>
        {activeTab === 'yearly' && (
          <YearSelector value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </YearSelector>
        )}
      </Header>

      <MonitoringContainer>
        {activeTab === 'yearly' && yearlyAnalytics ? (
          <>
            {/* Main Stats Cards */}
            <StatsGrid>
              <StatCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                <StatIcon>👥</StatIcon>
                <StatLabel>Nouveaux Inscrits {selectedYear}</StatLabel>
                <StatValue>{yearlyAnalytics.users.total}</StatValue>
                <StatSubtext>
                  {yearlyAnalytics.users.withHubspot} profils HubSpot associés
                </StatSubtext>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)">
                <StatIcon>🏭</StatIcon>
                <StatLabel>Fabricants / Producteurs</StatLabel>
                <StatValue>
                  {yearlyAnalytics.users.fabricants.total}
                  <StatPercentage>{yearlyAnalytics.users.fabricants.percentage}%</StatPercentage>
                </StatValue>
                <StatSubtext>
                  dont {yearlyAnalytics.users.fabricants.details.artisans} artisans d'art
                </StatSubtext>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)">
                <StatIcon>✨</StatIcon>
                <StatLabel>Créateurs</StatLabel>
                <StatValue>
                  {yearlyAnalytics.users.createurs.total}
                  <StatPercentage>{yearlyAnalytics.users.createurs.percentage}%</StatPercentage>
                </StatValue>
                <StatSubtext>
                  {yearlyAnalytics.users.createurs.details.designers} designers, {yearlyAnalytics.users.createurs.details.architectesInterieurs} architectes int.
                </StatSubtext>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
                <StatIcon>📁</StatIcon>
                <StatLabel>Nouveaux Projets {selectedYear}</StatLabel>
                <StatValue>{yearlyAnalytics.projects.total}</StatValue>
                <StatSubtext>
                  {yearlyAnalytics.projects.objet.percentage}% objet, {yearlyAnalytics.projects.espace.percentage}% espace
                </StatSubtext>
              </StatCard>
            </StatsGrid>

            {/* Charts */}
            <ChartGrid>
              <ChartCard>
                <ChartHeader>
                  <ChartTitle>Évolution Mensuelle</ChartTitle>
                  <ChartFilter value={chartView} onChange={(e) => setChartView(e.target.value)}>
                    <option value="users">Utilisateurs</option>
                    <option value="projects">Projets</option>
                  </ChartFilter>
                </ChartHeader>
                {chartView === 'users' ? (
                  <Line data={monthlyChartData} options={chartOptions} />
                ) : (
                  <Bar data={monthlyChartData} options={chartOptions} />
                )}
              </ChartCard>

              <ChartCard>
                <ChartHeader>
                  <ChartTitle>Répartition</ChartTitle>
                </ChartHeader>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                  <div>
                    <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontWeight: '600', color: '#444' }}>
                      Fabricants vs Créateurs
                    </div>
                    <DoughnutContainer>
                      <Doughnut data={userTypeChartData} options={doughnutOptions} />
                    </DoughnutContainer>
                    <LegendContainer>
                      <LegendItem>
                        <LegendDot color="#667eea" />
                        Fabricants ({yearlyAnalytics.users.fabricants.percentage}%)
                      </LegendItem>
                      <LegendItem>
                        <LegendDot color="#764ba2" />
                        Créateurs ({yearlyAnalytics.users.createurs.percentage}%)
                      </LegendItem>
                    </LegendContainer>
                  </div>
                  <div>
                    <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontWeight: '600', color: '#444' }}>
                      Objet vs Espace
                    </div>
                    <DoughnutContainer>
                      <Doughnut data={projectTypeChartData} options={doughnutOptions} />
                    </DoughnutContainer>
                    <LegendContainer>
                      <LegendItem>
                        <LegendDot color="#2ecc71" />
                        Objet ({yearlyAnalytics.projects.objet.percentage}%)
                      </LegendItem>
                      <LegendItem>
                        <LegendDot color="#3498db" />
                        Espace ({yearlyAnalytics.projects.espace.percentage}%)
                      </LegendItem>
                    </LegendContainer>
                  </div>
                </div>
              </ChartCard>
            </ChartGrid>

            {/* Detailed Breakdown */}
            <BreakdownSection>
              <BreakdownTitle>
                <span>Détail des Créateurs</span>
              </BreakdownTitle>
              <BreakdownGrid>
                <BreakdownItem color="#9b59b6">
                  <BreakdownLabel>Designers</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.createurs.details.designers}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#3498db">
                  <BreakdownLabel>Architectes d'intérieur</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.createurs.details.architectesInterieurs}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#e74c3c">
                  <BreakdownLabel>Scénographes</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.createurs.details.scenographes}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#f39c12">
                  <BreakdownLabel>Graphistes</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.createurs.details.graphistes}</BreakdownValue>
                </BreakdownItem>
              </BreakdownGrid>
            </BreakdownSection>

            <BreakdownSection>
              <BreakdownTitle>
                <span>Détail des Fabricants</span>
              </BreakdownTitle>
              <BreakdownGrid>
                <BreakdownItem color="#2ecc71">
                  <BreakdownLabel>Artisans d'art</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.fabricants.details.artisans}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#1abc9c">
                  <BreakdownLabel>Galeries</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.fabricants.details.galeries}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#16a085">
                  <BreakdownLabel>Autres Producteurs</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.fabricants.details.autresProducteurs}</BreakdownValue>
                </BreakdownItem>
              </BreakdownGrid>
            </BreakdownSection>

            <BreakdownSection>
              <BreakdownTitle>
                <span>Autres Profils</span> (non comptés dans le ratio)
              </BreakdownTitle>
              <BreakdownGrid>
                <BreakdownItem color="#95a5a6">
                  <BreakdownLabel>Donneurs d'ordre</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.autres.details.donneursDordre}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#7f8c8d">
                  <BreakdownLabel>Presse</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.autres.details.presse}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#bdc3c7">
                  <BreakdownLabel>Institutionnel</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.users.autres.details.institutionnel}</BreakdownValue>
                </BreakdownItem>
              </BreakdownGrid>
            </BreakdownSection>

            <BreakdownSection>
              <BreakdownTitle>
                <span>Détail des Projets</span>
              </BreakdownTitle>
              <BreakdownGrid>
                <BreakdownItem color="#2ecc71">
                  <BreakdownLabel>Projets Objet</BreakdownLabel>
                  <BreakdownValue>
                    {yearlyAnalytics.projects.objet.total}
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, marginLeft: '0.5rem', color: '#2ecc71' }}>
                      ({yearlyAnalytics.projects.objet.percentage}%)
                    </span>
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#3498db">
                  <BreakdownLabel>Projets Espace</BreakdownLabel>
                  <BreakdownValue>
                    {yearlyAnalytics.projects.espace.total}
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, marginLeft: '0.5rem', color: '#3498db' }}>
                      ({yearlyAnalytics.projects.espace.percentage}%)
                    </span>
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem color="#9b59b6">
                  <BreakdownLabel>Total Projets {selectedYear}</BreakdownLabel>
                  <BreakdownValue>{yearlyAnalytics.projects.total}</BreakdownValue>
                </BreakdownItem>
              </BreakdownGrid>
            </BreakdownSection>
          </>
        ) : activeTab === 'weekly' && analytics ? (
          <>
            {/* Weekly Stats - Original functionality */}
            <StatsGrid>
              <StatCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                <StatIcon>👥</StatIcon>
                <StatLabel>Total Utilisateurs</StatLabel>
                <StatValue>{analytics.totalUsers || 0}</StatValue>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)">
                <StatIcon>📈</StatIcon>
                <StatLabel>Nouveaux Cette Semaine</StatLabel>
                <StatValue>{analytics.newUsersThisWeek || 0}</StatValue>
                <StatSubtext>
                  vs {analytics.newUsersLastWeek || 0} semaine précédente
                </StatSubtext>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
                <StatIcon>📁</StatIcon>
                <StatLabel>Total Projets</StatLabel>
                <StatValue>{analytics.totalProjects || 0}</StatValue>
              </StatCard>

              <StatCard gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
                <StatIcon>🏢</StatIcon>
                <StatLabel>Entreprises Actives</StatLabel>
                <StatValue>{analytics.activeCompanies || 0}</StatValue>
              </StatCard>
            </StatsGrid>

            <ChartGrid>
              <ChartCard>
                <ChartTitle>Croissance Utilisateurs (8 Dernières Semaines)</ChartTitle>
                <Line
                  data={{
                    labels: analytics?.userGrowth?.labels || [],
                    datasets: [{
                      label: 'Nouveaux Utilisateurs',
                      data: analytics?.userGrowth?.data || [],
                      borderColor: '#667eea',
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      fill: true,
                      tension: 0.4,
                    }]
                  }}
                  options={chartOptions}
                />
              </ChartCard>

              <ChartCard>
                <ChartTitle>Nouveaux Projets (8 Dernières Semaines)</ChartTitle>
                <Bar
                  data={{
                    labels: analytics?.projectGrowth?.labels || [],
                    datasets: [{
                      label: 'Nouveaux Projets',
                      data: analytics?.projectGrowth?.data || [],
                      backgroundColor: 'rgba(240, 147, 251, 0.8)',
                      borderColor: '#f093fb',
                      borderWidth: 1,
                      borderRadius: 6,
                    }]
                  }}
                  options={chartOptions}
                />
              </ChartCard>
            </ChartGrid>
          </>
        ) : (
          <EmptyState>Aucune donnée disponible</EmptyState>
        )}
      </MonitoringContainer>
    </Section>
  );
};

export default Monitoring;
